import React, { useEffect, useMemo, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import useTranscriptViewer, { getAuthenticatedMediaUrl, lastIndexStartingAtOrBefore } from '../../hooks/useTranscriptViewer';
import useTranscriptSearch from '../../hooks/useTranscriptSearch';
import MediaPlayer from './MediaPlayer';
import TranscriptSearchBar from './TranscriptSearchBar';
import TranscriptParagraph from './TranscriptParagraph';
import './Viewer.css';


function TranscriptViewer() {
  const { id } = useParams();
  const {
    transcript,
    loading,
    error,
    currentTime,
    activeUtteranceIndex,
    mediaPlayerRef,
    transcriptContainerRef,
    groupedUtterances,
    handleTimeUpdate,
    handleDownload,
    handleDownloadMedia,
    copyAllText,
    handleWordClick,
  } = useTranscriptViewer(id);

  const search = useTranscriptSearch(groupedUtterances, handleWordClick);
  const searchInputRef = useRef(null);

  // Which paragraph holds the active utterance, and which word in it is being
  // spoken. Computed once per tick so only those paragraphs re-render.
  const groupIndexByUtterance = useMemo(() => {
    const map = [];
    groupedUtterances.forEach((group, gi) => {
      group.utterances.forEach((u) => { map[u.originalIndex] = gi; });
    });
    return map;
  }, [groupedUtterances]);

  const activeGroupIndex = groupIndexByUtterance[activeUtteranceIndex] ?? -1;

  const activeWordIndex = useMemo(() => {
    const words = groupedUtterances[activeGroupIndex]?.words;
    if (!words?.length) return -1;
    const ms = currentTime * 1000;
    const wi = lastIndexStartingAtOrBefore(words, ms);
    return wi >= 0 && ms < words[wi].end ? wi : -1;
  }, [groupedUtterances, activeGroupIndex, currentTime]);

  // Ctrl/Cmd+F focuses transcript search; pressing it again while the
  // search box is focused falls through to the browser's own find.
  useEffect(() => {
    const onKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'f') {
        const input = searchInputRef.current;
        if (input && document.activeElement !== input) {
          e.preventDefault();
          input.focus();
          input.select();
        }
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  if (loading) {
    return (
      <div className="viewer-container">
        <div className="loading">Loading transcript...</div>
      </div>
    );
  }

  if (error || !transcript) {
    return (
      <div className="viewer-container">
        <div className="alert alert-error">{error || 'Transcript not found'}</div>
        <Link to="/files" className="btn btn-secondary">Back to Files</Link>
      </div>
    );
  }

  if (transcript.status !== 'completed') {
    return (
      <div className="viewer-container">
        <div className="viewer-status card">
          <h2>{transcript.original_filename}</h2>
          <p className={`status-badge status-${transcript.status}`}>
            {transcript.status}
          </p>
          {transcript.status === 'processing' && (
            <p>Transcription in progress. Please wait...</p>
          )}
          {transcript.status === 'failed' && (
            <p>Transcription failed. Please try again.</p>
          )}
          <Link to="/files" className="btn btn-secondary">Back to Files</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="viewer-container">
      <div className="viewer-header">
        <div className="viewer-title-section">
          <Link to="/files" className="back-link">&larr; Back</Link>
          <h1 className="viewer-title">{transcript.original_filename}</h1>
        </div>

        <div className="viewer-actions">
          <button className="btn btn-secondary btn-sm" onClick={copyAllText}>
            Copy All
          </button>
          <button className="btn btn-secondary btn-sm" onClick={() => handleDownload('txt')}>
            Download .txt
          </button>
          <button className="btn btn-secondary btn-sm" onClick={() => handleDownload('json')}>
            Download .json
          </button>
          {transcript.media_url && (
            <button className="btn btn-secondary btn-sm" onClick={handleDownloadMedia}>
              Download Media
            </button>
          )}
        </div>
      </div>

      <div className="viewer-split-layout">
        <div className="viewer-video-panel">
          {transcript.media_url ? (
            <MediaPlayer
              ref={mediaPlayerRef}
              src={getAuthenticatedMediaUrl(transcript.media_url)}
              onTimeUpdate={handleTimeUpdate}
            />
          ) : (
            <div className="media-unavailable">The original media file is no longer available. The transcript can still be viewed and downloaded.</div>
          )}
        </div>

        <div
          ref={transcriptContainerRef}
          className="viewer-transcript-panel"
        >
          <div className="viewer-search-sticky">
            <TranscriptSearchBar
              ref={searchInputRef}
              search={search}
              placeholder="Search transcript (Ctrl+F)"
            />
          </div>
          <div className="transcript-content">
            {groupedUtterances.map((group, index) => (
              <TranscriptParagraph
                key={index}
                group={group}
                groupIndex={index}
                isActive={index === activeGroupIndex}
                activeWordIndex={index === activeGroupIndex ? activeWordIndex : -1}
                getWordSearchClass={search.getWordSearchClass}
                onSeek={handleWordClick}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default TranscriptViewer;
